/**
 * ResetPasswordPage — landing target for password recovery emails.
 *
 * Establishes a session from the link, then lets the user set a new
 * password. Three arrival shapes are supported:
 *
 *   1. `?token_hash=…&type=recovery` — custom recovery email template.
 *      Verified via `verifyOtp`, which works even when the link is
 *      opened in a different browser than the one that requested it.
 *   2. `?code=…` — default Supabase PKCE link. The client exchanges it
 *      automatically (detectSessionInUrl); we wait for the session.
 *      Only works in the browser that requested the reset.
 *   3. `?error=…` / no params — expired or invalid link → offer to
 *      request a new one. An already-signed-in user visiting directly
 *      gets the form (doubles as a change-password screen).
 */
import { useEffect, useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { AuthCard } from '../components/AuthCard';

const MIN_PASSWORD_LENGTH = 8;
/** How long to wait for the PKCE ?code= exchange before declaring the link invalid. */
const CODE_EXCHANGE_TIMEOUT_MS = 10_000;

type Stage = 'verifying' | 'form' | 'invalid';

export function ResetPasswordPage() {
  const navigate = useNavigate();
  const [stage, setStage] = useState<Stage>('verifying');
  const [linkError, setLinkError] = useState<string | null>(null);
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [formError, setFormError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    // Recovery params may arrive in the query string or the hash
    // fragment depending on which flow produced the link.
    const search = new URLSearchParams(window.location.search);
    const hash = new URLSearchParams(window.location.hash.replace(/^#/, ''));
    const param = (key: string) => search.get(key) ?? hash.get(key);

    const errorDescription = param('error_description') ?? param('error');
    const tokenHash = param('token_hash');
    const type = param('type');
    const code = param('code');

    // Once verified, drop one-time tokens from the URL so a refresh
    // doesn't re-submit a consumed token and flash an error.
    const cleanUrl = () =>
      window.history.replaceState(null, '', window.location.pathname);

    if (errorDescription) {
      setLinkError(errorDescription.replace(/\+/g, ' '));
      setStage('invalid');
      return;
    }

    if (tokenHash && type === 'recovery') {
      supabase.auth
        .verifyOtp({ type: 'recovery', token_hash: tokenHash })
        .then(({ error }) => {
          if (error) {
            setLinkError(error.message);
            setStage('invalid');
          } else {
            cleanUrl();
            setStage('form');
          }
        });
      return;
    }

    if (code) {
      // detectSessionInUrl performs the exchange during client init;
      // wait for the resulting session rather than exchanging again.
      let settled = false;
      const settle = (ok: boolean) => {
        if (settled) return;
        settled = true;
        if (ok) {
          cleanUrl();
          setStage('form');
        } else {
          setLinkError(
            'This link could not be verified. Reset links only work in the browser they were requested from.',
          );
          setStage('invalid');
        }
      };

      const {
        data: { subscription },
      } = supabase.auth.onAuthStateChange((_event, session) => {
        if (session) settle(true);
      });
      supabase.auth.getSession().then(({ data: { session } }) => {
        if (session) settle(true);
      });
      const timer = setTimeout(() => settle(false), CODE_EXCHANGE_TIMEOUT_MS);

      return () => {
        subscription.unsubscribe();
        clearTimeout(timer);
      };
    }

    // No recovery params — only useful if already signed in.
    supabase.auth.getSession().then(({ data: { session } }) => {
      setStage(session ? 'form' : 'invalid');
    });
  }, []);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setFormError(null);

    if (password.length < MIN_PASSWORD_LENGTH) {
      setFormError(`Password must be at least ${MIN_PASSWORD_LENGTH} characters.`);
      return;
    }
    if (password !== confirm) {
      setFormError('Passwords do not match.');
      return;
    }

    setSaving(true);
    const { error } = await supabase.auth.updateUser({ password });
    setSaving(false);

    if (error) {
      setFormError(error.message);
    } else {
      navigate('/dashboard', { replace: true });
    }
  };

  if (stage === 'verifying') {
    return (
      <AuthCard>
        <div className="flex flex-col items-center gap-3 py-6">
          <div className="w-8 h-8 border-3 border-primary border-t-transparent rounded-full animate-spin" />
          <p className="text-xs" style={{ color: 'var(--text-muted)' }}>
            Verifying your reset link…
          </p>
        </div>
      </AuthCard>
    );
  }

  if (stage === 'invalid') {
    return (
      <AuthCard>
        <div className="text-center py-4">
          <div
            className="w-12 h-12 mx-auto mb-3 rounded-full flex items-center justify-center"
            style={{ backgroundColor: 'var(--error-soft)' }}
          >
            <svg className="w-6 h-6" style={{ color: 'var(--error)' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v3.75m0 3.75h.008v.008H12v-.008zM21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <p className="text-sm font-medium" style={{ color: 'var(--text)' }}>
            This reset link is invalid or has expired
          </p>
          {linkError && (
            <p className="text-xs mt-1" style={{ color: 'var(--text-muted)' }}>
              {linkError}
            </p>
          )}
          <Link
            to="/forgot-password"
            className="public-btn-primary inline-flex mt-4"
          >
            Request a new link
          </Link>
          <p className="mt-3">
            <Link
              to="/login"
              className="text-xs underline"
              style={{ color: 'var(--text-faint)' }}
            >
              Back to sign in
            </Link>
          </p>
        </div>
      </AuthCard>
    );
  }

  return (
    <AuthCard>
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <p className="text-lg font-semibold" style={{ color: '#1F1E26' }}>
            Set a new password
          </p>
          <p className="text-sm mt-1" style={{ color: '#6B6A75' }}>
            Must be at least {MIN_PASSWORD_LENGTH} characters.
          </p>
        </div>

        <div>
          <label htmlFor="new-password" className="public-label">
            New password
          </label>
          <input
            id="new-password"
            type="password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="new-password"
            className="public-input"
          />
        </div>

        <div>
          <label htmlFor="confirm-password" className="public-label">
            Confirm password
          </label>
          <input
            id="confirm-password"
            type="password"
            required
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            autoComplete="new-password"
            className="public-input"
          />
        </div>

        {formError && (
          <div className="public-error">
            <p>{formError}</p>
          </div>
        )}

        <button
          type="submit"
          disabled={saving || !password || !confirm}
          className="public-btn-primary w-full justify-center disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {saving ? 'Saving...' : 'Set new password'}
        </button>
      </form>
    </AuthCard>
  );
}
